/**
 * The standard date ranges the analysis screens offer (dictated 2026-10-17).
 *
 * “This …” runs from the start of the period **to today** — never into the future, so a
 * month view never shows days that have not happened yet. “Last …” is the full previous
 * period. “All time” starts at the first day we model, because a mock dataset cannot answer
 * for a period before it exists.
 */

export interface DateRange {
  from: string
  to: string
  /** The preset the user picked, kept so identical dates (Today vs This week) stay unambiguous. */
  preset?: RangePresetLabel
}

/** First day the (mock) history covers. */
export const EARLIEST_DATA_DAY = '2025-01-01'

/** Local calendar day, never `toISOString()` — that would shift the date west of UTC. */
const iso = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Local midnight for a calendar date (the shop's timezone, not UTC). */
const localDay = (year: number, month: number, day: number): Date => new Date(year, month, day)

/** Monday of the week containing `now` (ISO weeks: the working week starts on Monday). */
function startOfWeek(now: Date): Date {
  const offset = (now.getDay() + 6) % 7
  return localDay(now.getFullYear(), now.getMonth(), now.getDate() - offset)
}

/** First day of the calendar quarter containing `now`. */
function startOfQuarter(now: Date): Date {
  return localDay(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)
}

const lastDayOfMonth = (year: number, month: number): Date => new Date(year, month + 1, 0)

export interface RangePreset {
  label: string
  from: string
  to: string
}

export const RANGE_PRESET_LABELS = [
  'Today',
  'Yesterday',
  'This week',
  'Last week',
  'This month',
  'Last month',
  'This quarter',
  'Last quarter',
  'This year',
  'Last year',
  'All time',
] as const

export type RangePresetLabel = (typeof RANGE_PRESET_LABELS)[number]

/** All eleven presets, in the order the GM listed them, plus Today/Yesterday. */
export function rangePresets(now = new Date()): RangePreset[] {
  const year = now.getFullYear()
  const month = now.getMonth()
  const today = iso(now)
  const todayDate = localDay(year, month, now.getDate())
  const yesterday = localDay(year, month, now.getDate() - 1)

  const weekStart = startOfWeek(now)
  const lastWeekStart = localDay(
    weekStart.getFullYear(),
    weekStart.getMonth(),
    weekStart.getDate() - 7,
  )
  const lastWeekEnd = localDay(
    weekStart.getFullYear(),
    weekStart.getMonth(),
    weekStart.getDate() - 1,
  )

  const quarterStart = startOfQuarter(now)
  const quarterFloor = Math.floor(month / 3) * 3
  const lastQuarterStart = localDay(year, quarterFloor - 3, 1)
  const lastQuarterEnd = localDay(year, quarterFloor, 0)

  return [
    { label: 'Today', from: iso(todayDate), to: iso(todayDate) },
    { label: 'Yesterday', from: iso(yesterday), to: iso(yesterday) },
    { label: 'This week', from: iso(weekStart), to: today },
    { label: 'Last week', from: iso(lastWeekStart), to: iso(lastWeekEnd) },
    { label: 'This month', from: iso(localDay(year, month, 1)), to: today },
    {
      label: 'Last month',
      from: iso(localDay(year, month - 1, 1)),
      to: iso(lastDayOfMonth(year, month - 1)),
    },
    { label: 'This quarter', from: iso(quarterStart), to: today },
    { label: 'Last quarter', from: iso(lastQuarterStart), to: iso(lastQuarterEnd) },
    { label: 'This year', from: iso(localDay(year, 0, 1)), to: today },
    { label: 'Last year', from: iso(localDay(year - 1, 0, 1)), to: iso(localDay(year - 1, 11, 31)) },
    { label: 'All time', from: EARLIEST_DATA_DAY, to: today },
  ]
}

/** Which preset the current range corresponds to, or null for a hand-picked range. */
export function matchingPreset(
  range: DateRange,
  now = new Date(),
): RangePresetLabel | null {
  const presets = rangePresets(now)
  const preferred = range.preset
    ? presets.find(
        (preset) =>
          preset.label === range.preset &&
          preset.from === range.from &&
          preset.to === range.to,
      )
    : undefined
  if (preferred) return preferred.label as RangePresetLabel

  const match = presets.find(
    (preset) => preset.from === range.from && preset.to === range.to,
  )
  return (match?.label as RangePresetLabel | undefined) ?? null
}

/** “1 Oct 2026 – 17 Oct 2026”, the label shown beside the filters. */
export function formatRange(range: DateRange): string {
  const format = (value: string) =>
    new Date(`${value}T00:00:00Z`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  return `${format(range.from)} – ${format(range.to)}`
}

/** The local calendar day as `YYYY-MM-DD` — the same form `DateRange` uses. */
export function isoDay(day: Date): string {
  return iso(day)
}

/**
 * True when the range covers exactly one day and that day is `day` (D-110).
 *
 * The General Overview needs this to avoid showing “Live Sales (Today)” and “Sales
 * Today” side by side: they are the same figure when the period is a single trading
 * day, so the period card steps aside and the always-present “Sales Today” card
 * carries the shops-reporting hint instead.
 */
export function isSingleDay(range: DateRange, day: string): boolean {
  return range.from === day && range.to === day
}
